// ===== BASE SERVICE =====
import { tokenManager } from './TokenManager';
import { isCapacitor, isElectron } from '../utils/platform';  
import { offlineManager } from './OfflineManager';
import { Preferences } from '@capacitor/preferences';
import { tokenRecoveryService } from './TokenRecoveryService';
import { tokenLog, tokenWarn, tokenError } from './Debug';

// Token refresh promise to prevent race conditions
let refreshPromise = null;
let refreshAttempts = 0;
let lastRefreshTime = 0;

// Global usage limit state
let usageLimitState = {
  isLimitReached: false,
  limitDetails: null,
  listeners: new Set()
};

// Dispatch a global API error event for centralized UI handling
function sanitizeUserMessage(rawMessage) {
  try {
    const message = String(rawMessage || '');
    if (/\b(http|token)\b/i.test(message)) {
      return 'Une erreur est survenue. Veuillez réessayer.';
    }
    return message;
  } catch (_) {
    return 'Une erreur est survenue. Veuillez réessayer.';
  }
}

function dispatchApiError(message, meta = {}) {
  try {
    // Filter out generic/technical error messages (90% margin)
    const messageStr = String(message || '');

    // Don't show HTTP errors, Load failed messages, or other technical errors
    if (messageStr.includes('HTTP error') ||
        messageStr.includes('Load failed') ||
        messageStr.includes('Failed to fetch') ||
        messageStr.includes('Network request failed') ||
        messageStr.includes('fetch failed') ||
        messageStr.includes('Unknown error') ||
        messageStr.toLowerCase().includes('refresh') ||
        messageStr.toLowerCase().includes('token')) {
      return; // Don't dispatch these errors to UI
    }

    if (typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('api:error', {
        detail: {
          message: messageStr || 'Unknown error',
          timestamp: Date.now(),
          ...meta
        }
      }));
    }
  } catch (_) {
    // best-effort only
  }
}

// Centralized refresh function
async function doTokenRefresh() {
  // Check if we've exceeded max refresh attempts
  if (refreshAttempts >= 10) {
    tokenError('doTokenRefresh: max refresh attempts exceeded');
    refreshAttempts = 0; // Reset for next session
    
    // Dispatch session expired event
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('session:expired', {
        detail: { reason: 'max_refresh_attempts' }
      }));
    }
    
    throw new Error('Max refresh attempts exceeded');
  }
  
  refreshAttempts++;
  tokenLog('doTokenRefresh: attempting refresh', { attempt: refreshAttempts });
  
  try {
    const { UserService } = await import('./UserService.js');
    const userService = new UserService();
    const newToken = await userService.refreshToken();
    
    // Reset attempts on success
    refreshAttempts = 0;
    lastRefreshTime = Date.now();
    tokenLog('doTokenRefresh: refresh succeeded');
    return newToken;
  } catch (error) {
    tokenError('doTokenRefresh: token refresh failed', error);
    
    // On iOS, if refresh fails due to missing token, attempt recovery
    if (isCapacitor && error.message && error.message.includes('No refresh token available')) {
      tokenWarn('doTokenRefresh: iOS refresh token lost, attempting recovery');
      tokenRecoveryService.logError('Refresh Failed', error);
      
      const recovered = await tokenRecoveryService.attemptTokenRecovery();
      if (recovered) {
        // Retry the refresh after recovery
        refreshAttempts--; // Don't count this attempt
        return doTokenRefresh();
      } else {
        tokenWarn('doTokenRefresh: token recovery failed');
        
        // Dispatch session expired event with specific reason
        if (typeof window !== 'undefined' && window.dispatchEvent) {
          window.dispatchEvent(new CustomEvent('session:expired', {
            detail: { reason: 'no_refresh_token' }
          }));
        }
        
        // Don't clear tokens immediately - let user decide
        // await tokenManager.clear();
      }
    } else {
      // For other refresh errors, also dispatch session expired
      if (typeof window !== 'undefined' && window.dispatchEvent) {
        window.dispatchEvent(new CustomEvent('session:expired', {
          detail: { reason: 'refresh_failed', error: error.message }
        }));
      }
    }
    
    throw error;
  }
}

// Reset refresh attempts (useful for testing or when manually logging in)
export function resetRefreshAttempts() {
  refreshAttempts = 0;
  refreshPromise = null;
  lastRefreshTime = 0;
}

export class BaseService {
  
  // Static methods for usage limit management
  static setUsageLimit(isReached, details = null) {
    usageLimitState.isLimitReached = isReached;
    usageLimitState.limitDetails = details;
    
    // Notify all listeners
    usageLimitState.listeners.forEach(listener => {
      listener(isReached, details);
    });
  }
  
  static getUsageLimitState() {
    return {
      isLimitReached: usageLimitState.isLimitReached,
      limitDetails: usageLimitState.limitDetails
    };
  }
  
  static addUsageLimitListener(listener) {
    usageLimitState.listeners.add(listener);
    return () => usageLimitState.listeners.delete(listener);
  }
  
  constructor(baseURL) {
    // Use environment variable if available
    this.baseURL = process.env.REACT_APP_API_BASE_URL || baseURL || 'http://localhost:3001/api';
    
    // Ensure it's an absolute URL
    if (!this.baseURL.startsWith('http://') && !this.baseURL.startsWith('https://')) {
      // If it's a relative path and we're in Electron, use a default
      if (window.location.protocol === 'file:') {
        this.baseURL = 'http://localhost:3001/api';
      } else {
        this.baseURL = window.location.origin + this.baseURL;
      }
    }
    
    // Remove trailing slash
    this.baseURL = this.baseURL.replace(/\/$/, '');
  }

  async request(url, options = {}) {
    // Extract custom options
    const { 
      skipAuth = false, 
      responseType = 'json',
      skipContentType = false,
      ...fetchOptions 
    } = options;

    if (!skipAuth) {
      await this.ensureFreshToken();
    }

    const token = tokenManager.getToken();

    // Prepare headers
    const headers = {
      ...fetchOptions.headers
    };

    // Don't add Content-Type for FormData
    if (!skipContentType && !(fetchOptions.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    // Add token if necessary
    if (!skipAuth && token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      // Ensure URL starts with /
      if (!url.startsWith('/')) {
        url = '/' + url;
      }
      
      const fullUrl = `${this.baseURL}${url}`;
      
      // Prepare body
      let body = fetchOptions.body;
      
      // Stringify objects that aren't FormData/Blob/etc
      if (body && 
          typeof body === 'object' && 
          !(body instanceof FormData) && 
          !(body instanceof Blob) && 
          !(body instanceof ArrayBuffer) &&
          !(body instanceof URLSearchParams)) {
        body = JSON.stringify(body);
      }
      
      const response = await fetch(fullUrl, {
        credentials: 'include',
        ...fetchOptions,
        headers,
        body
      });

      // Handle different response types
      let data;
      const contentType = response.headers.get('content-type');
      
      if (!response.ok) {
        tokenWarn('HTTP error response', { 
          status: response.status, 
          headers: [...response.headers.entries()] 
        });
        
        let errorData;
        let errorText;
        
        try {
          const clonedResponse = response.clone();
          
          try {
            errorData = await response.json();
          } catch (jsonError) {
            errorText = await clonedResponse.text();  
            tokenError('Failed to parse error as JSON, got text:', errorText);
          }
          
          if (errorData) {
            // Handle 429 Too Many Requests
            if (response.status === 429) {
              if (errorData.code === 'LIMIT_EXCEEDED') {
                BaseService.setUsageLimit(true, errorData.details);

                const error = new Error(errorData.error || 'Daily limit exceeded');
                error.code = errorData.code;
                error.details = errorData.details;
                error.success = errorData.success;
                // Notify globally for UI alert
                dispatchApiError(error.message, { status: response.status, code: error.code, details: error.details });
                throw error;
              } else {
                // Rate limiting de sécurité (429 générique)
                const rateLimitMessage = "Vous avez fait trop de requêtes à notre serveur, votre accès est bloqué 1 minute par sécurité.";
                const error = new Error(rateLimitMessage);
                error.code = 'RATE_LIMITED';
                error.details = errorData.details;
                dispatchApiError(rateLimitMessage, { status: response.status, code: 'RATE_LIMITED' });
                throw error;
              }
            }
            
            // Handle 401 Unauthorized
            if (!skipAuth && response.status === 401 && !fetchOptions._retry) {
              
              // Check if we're offline first
              if (!offlineManager.getIsOnline()) {
                const error = new Error('No internet connection');
                error.code = 'OFFLINE';
                error.isOffline = true;
                throw error;
              }
              
              fetchOptions._retry = true;
              options._retry = true;
              
              try {
                // Use the SAME shared refresh promise
                if (!refreshPromise) {
                  refreshPromise = doTokenRefresh().finally(() => {
                    refreshPromise = null;
                  });
                }
                
                await refreshPromise;
                
                // After successful refresh, retry the original request
                tokenLog('401 handler: retrying original request after refresh');
                return this.request(url, options);
             // In the request() method, update the 401 handler catch block:
} catch (refreshError) {
  tokenError('401 handler: token refresh failed', refreshError);
  
  if (!offlineManager.getIsOnline()) {
    const error = new Error('Cannot refresh token while offline');
    error.code = 'OFFLINE_AUTH';
    error.isOffline = true;
    throw error;
  } else {
    // Check if we're in the middle of logout
    const authManager = require('./AuthManager').authManager;
    if (!authManager.isLoggingOut) {
      // The session:expired event is already dispatched by doTokenRefresh
      // Just rethrow the error
    }
    throw refreshError;
  }
}
            }
            
            // Create error with API structure
            const error = new Error((typeof errorData.error === 'string' && errorData.error) || (typeof errorData.message === 'string' && errorData.message) || `HTTP error! status: ${response.status}`);
            if (errorData.code) error.code = errorData.code;
            if (errorData.details) error.details = errorData.details;
            if (errorData.success !== undefined) error.success = errorData.success;
            // Add response structure for compatibility with axios-style error handling
            error.response = {
              data: errorData,
              status: response.status,
              statusText: response.statusText
            };
            // Notify globally (exclude 401 which is handled by session flow)
            if (response.status !== 401) {
              dispatchApiError(error.message, { status: response.status, code: error.code, details: error.details });
            }
            throw error;
          } else {
            // Handle 429 without JSON body
            if (response.status === 429) {
              const rateLimitMessage = "Vous avez fait trop de requêtes à notre serveur, votre accès est bloqué 1 minute par sécurité.";
              const error = new Error(rateLimitMessage);
              error.code = 'RATE_LIMITED';
              dispatchApiError(rateLimitMessage, { status: response.status, code: 'RATE_LIMITED' });
              throw error;
            }
            const textError = new Error(errorText || `HTTP error! status: ${response.status}`);
            // Add response structure for compatibility
            textError.response = {
              data: { message: errorText || `HTTP error! status: ${response.status}` },
              status: response.status,
              statusText: response.statusText
            };
            //dispatchApiError(textError.message, { status: response.status });
            throw textError;
          }
          
        } catch (error) {
          // Re-throw if already formatted
          if (error.code || error.details) {
            throw error;
          }
          tokenError('Error handling response', error);
          const genericError = new Error(`HTTP error! status: ${response.status}`);
          // Add response structure for compatibility
          genericError.response = {
            data: { message: `HTTP error! status: ${response.status}` },
            status: response.status,
            statusText: response.statusText
          };
          //dispatchApiError(genericError.message, { status: response.status });
          throw genericError;
        }
      }
      
      // Handle OK responses by type
      switch (responseType) {
        case 'blob':
          data = await response.blob();
          break;
          
        case 'arrayBuffer':
          data = await response.arrayBuffer();
          break;
          
        case 'text':
          data = await response.text();
          break;
          
        case 'json':
        default:
          if (contentType && contentType.includes('application/json')) {
            const text = await response.text();
            if (text.trim()) {
              try {
                data = JSON.parse(text);
              } catch (jsonError) {
                tokenError('JSON parsing error:', jsonError, 'Response text:', text);
                throw new Error(`Invalid JSON response: ${text.substring(0, 100)}`);
              }
            } else {
              data = {};
            }
          } else {
            const text = await response.text();
            tokenError('Expected JSON but received:', contentType);
            throw new Error(`Expected JSON response but got ${contentType}`);
          }
          break;
      }

      return data;
      
    } catch (error) {
      // Check if it's a network error
      if (error.message === 'Failed to fetch' || 
          error.message === 'Network request failed' || 
          error.type === 'network-error') {
        const networkError = new Error('No internet connection');
        networkError.code = 'NETWORK_ERROR';
        networkError.isOffline = true;
        dispatchApiError(networkError.message, { code: networkError.code, offline: true });
        throw networkError;
      }
      
      tokenError('API Request failed', error);
      // Fallback global notification
      try {
        if (error && error.message && error.code !== 'OFFLINE' && error.code !== 'OFFLINE_AUTH') {
          dispatchApiError(error.message);
        }
      } catch (_) {}
      throw error;
    }
  }

  // Helper methods for different request types
  get(url, options = {}) {
    return this.request(url, { ...options, method: 'GET' });
  }
  
  post(url, data, options = {}) {
    return this.request(url, {
      ...options,
      method: 'POST',
      body: data
    });
  }
  
  put(url, data, options = {}) {
    return this.request(url, {
      ...options,
      method: 'PUT',
      body: data
    });
  }
  
  patch(url, data, options = {}) {
    return this.request(url, {
      ...options,
      method: 'PATCH',
      body: data
    });
  }
  
  delete(url, options = {}) {
    return this.request(url, { ...options, method: 'DELETE' });
  }
  
  // Specialized methods for binaries
  postFormData(url, formData, options = {}) {
    return this.request(url, {
      ...options,
      method: 'POST',
      body: formData,
      skipContentType: true
    });
  }
  
  postBlob(url, data, options = {}) {
    return this.request(url, {
      ...options,
      method: 'POST',
      body: data,
      responseType: 'blob'
    });
  }
  
  getBlob(url, options = {}) {
    return this.request(url, {
      ...options,
      method: 'GET',
      responseType: 'blob'
    });
  }

  async ensureFreshToken() {
    if (!tokenManager.getToken()) {  
      return;
    }
    
    // Prevent refresh if we just refreshed recently
    const timeSinceLastRefresh = Date.now() - lastRefreshTime;
    if (timeSinceLastRefresh < 10000) {
      tokenLog('ensureFreshToken: skipping, refreshed recently', { timeSinceLastRefresh });
      return;
    }
    
    // Only refresh if the token is expiring soon < 1 min
    if (!tokenManager.isExpiringSoon()) return;

    // Use shared refresh promise
    if (!refreshPromise) {
      refreshPromise = doTokenRefresh().finally(() => {
        refreshPromise = null;
      });
    }
    
    return refreshPromise;
  }
}